import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { useEffect, useState } from 'react'

// axios 인터셉터 설정 및 QueryClient 연동
import { setQueryClient } from './utils/axiosConfig'
import { isWebView } from './utils/webView'
import { handlePostLoginFcmSetup } from './utils/fcmTokenManager'
import PasscodeScreen from './components/common/PasscodeScreen'
import { useAppStore } from './store/appStore'
import { useAuthStore } from './store/authStore'
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS, safeLocalStorage, safeSessionStorage } from './constants/storageKeys'
import { initializeNativeBridgeListeners } from './utils/nativeBridge'
import { decodeJWT } from './utils/jwt'

import { AuthLayout, BlankLayout, MainLayout } from './layouts'
import ProtectedRoute from './components/common/ProtectedRoute'
import ErrorBoundary from './components/common/ErrorBoundary'
import MissionPage from './pages/Mission/MissionPage'
import NoticePage from './pages/Notice/NoticePage'
import LoginPage from './pages/Auth/Login/LoginPage'
import ChatMainPage from './pages/Chat/ChatMainPage'
import ChatSelectManagerPage from './pages/Chat/ChatSelectManagerPage'
import NonStreamingChatRoom from './pages/Chat/noneStreamingChatRoomPage'
import RoomCheckPage from './pages/Chat/RoomCheckPage'
import MyPage from './pages/My/MyPage'
import ReportPage from './pages/Report/ReportPage'
import HomePage from './pages/Home/HomePage'
import ReportDetailPage from './pages/Report/ReportDetailPage'
import TypeTestMainPage from './pages/TypeTest/TypeTestMainPage'
import TypeTestQuestionsPage from './pages/TypeTest/TypeTestQuestionsPage'
import TypeTestResultPage from './pages/TypeTest/TypeTestResultPage'
// import BridgeTestPage from './pages/BridgeTest/BridgeTestPage'
import PrivacyPage from './pages/My/InnerPage/PrivacyPage'
import ServiceUsagePage from './pages/My/InnerPage/ServiceUsagePage'
import ProfileSettingPage from './pages/My/InnerPage/ProfileSettingPage'
import PasswordSettingPage from './pages/My/InnerPage/PasswordSettingPage'
import SecessionPage from './pages/My/InnerPage/SecessionPage'
import PushSettingPage from './pages/My/InnerPage/PushSettingPage'
import MissionAllPage from './pages/Mission/MissionAllPage'
import MissionDetailPage from './pages/Mission/[id]/MissionDetailPage'
import MissionCreatePage from './pages/Mission/MissionCreatePage'
import MissionEditPage from './pages/Mission/[id]/MissionEditPage'
import MySelectCounselor from './pages/My/InnerPage/MySelectCounselor'
import SignupCheckPage from './pages/Auth/SignupCheck/SignupCheckPage'
import TosPage from './pages/Auth/Tos/TosPage'
import SignupPage from './pages/Auth/Signup/SignupPage'
import NotFoundPage from './pages/Error/NotFoundPage'
import ErrorPage from './pages/Error/ErrorPage'
import TestLoginPage from './pages/Auth/Login/TestLoginPage'
import TestSignupCheckPage from './pages/Auth/SignupCheck/TestSignupCheckPage'
// import TestPage from './pages/Test/TestPage'
// import TestLayout from './layouts/TestLayout/TestLayout'
// import NotificationPage from './pages/Notification/NotificationPage'
// import DesignTestPage from './pages/DesignTest/DesignTestPage'

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '',
        element: <HomePage />
      },
      {
        path: 'mission',
        element: <MissionPage />
      }
      // {
      //   path: 'bridge-test',
      //   element: <BridgeTestPage />
      // }
    ]
  },
  {
    path: '/report',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '',
        element: <ReportPage />
      },
      {
        path: ':reportId',
        element: <ReportDetailPage />
      }
    ]
  },
  {
    path: '/type-test',
    element: (
      <ProtectedRoute>
        <BlankLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '',
        element: <TypeTestMainPage />
      },
      {
        path: 'questions',
        element: <TypeTestQuestionsPage />
      },
      {
        path: 'result',
        element: <TypeTestResultPage />
      }
    ]
  },
  {
    path: '/chat',
    element: (
      <ProtectedRoute>
        <MainLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '',
        element: <ChatMainPage />
      }
    ]
  },
  {
    path: '/chat',
    element: (
      <ProtectedRoute>
        <BlankLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: ':chatId',
        element: <NonStreamingChatRoom />
      }
    ]
  },
  {
    path: '/chat-detail',
    element: (
      <ProtectedRoute>
        <BlankLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: ':chatId',
        element: <NonStreamingChatRoom />
      }
    ]
  },
  {
    path: '/chat-prepare',
    element: (
      <ProtectedRoute>
        <BlankLayout />
      </ProtectedRoute>
    ),
    children: [{ path: '', element: <RoomCheckPage /> }]
  },

  {
    path: '/select-manager',
    element: (
      <ProtectedRoute>
        <BlankLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <ChatSelectManagerPage />
      }
    ]
  },
  {
    path: '/notice',
    element: (
      <ProtectedRoute>
        <BlankLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '',
        element: <NoticePage />
      }
    ]
  },
  // {
  //   path: '/notification',
  //   element: <BlankLayout />,
  //   children: [
  //     {
  //       path: '',
  //       element: <NotificationPage />
  //     }
  //   ]
  // },
  {
    path: '/auth',
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: 'login',
        element: <LoginPage />
      },
      {
        path: 'test-login',
        element: <TestLoginPage />
      },
      {
        path: 'signup-check/:provider',
        element: <SignupCheckPage />
      },
      {
        path: 'test-signup-check',
        element: <TestSignupCheckPage />
      },
      {
        path: 'tos',
        element: <TosPage />
      },
      {
        path: 'signup',
        element: <SignupPage />
      }
    ]
  },
  // {
  //   path: '/test',
  //   element: <TestLayout />,
  //   children: [
  //     {
  //       path: '',
  //       element: <TestPage />
  //     }
  //   ]
  // },
  // {
  //   path: '/design-test',
  //   element: <BlankLayout />,
  //   children: [
  //     {
  //       path: '',
  //       element: <DesignTestPage />
  //     }
  //   ]
  // },
  {
    path: '/my',
    element: (
      <ProtectedRoute>
        <BlankLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '',
        element: <MyPage />
      },
      {
        path: 'service-usage',
        element: <ServiceUsagePage />
      },
      {
        path: 'profile-setting',
        element: <ProfileSettingPage />
      },
      {
        path: 'password-setting',
        element: <PasswordSettingPage />
      },
      {
        path: 'secession',
        element: <SecessionPage />
      },
      {
        path: 'push-setting',
        element: <PushSettingPage />
      },
      {
        path: 'select-counselor',
        element: <MySelectCounselor />
      }
    ]
  },
  // 공개 페이지 (인증 불필요) - 스토어 심사용
  {
    path: '/my/privacy',
    element: <BlankLayout />,
    children: [
      {
        path: '',
        element: <PrivacyPage />
      }
    ]
  },
  {
    path: '/mission',
    element: (
      <ProtectedRoute>
        <BlankLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: 'all',
        element: <MissionAllPage />
      },
      {
        path: 'create/new',
        element: <MissionCreatePage />
      },
      {
        path: 'detail/:id',
        element: <MissionDetailPage />
      },
      {
        path: 'edit/:id',
        element: <MissionEditPage />
      }
    ]
  },
  // 404 페이지 (모든 정의되지 않은 경로)
  {
    path: '*',
    element: <NotFoundPage />
  }
])

// Create a QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5분
      gcTime: 10 * 60 * 1000, // 10분
      // 기본 에러 핸들러는 ErrorProvider에서 처리하도록 비활성화
      throwOnError: false
    },
    mutations: {
      retry: 1,
      // 기본 에러 핸들러는 ErrorProvider에서 처리하도록 비활성화
      throwOnError: false
    }
  }
})

// axios 인터셉터에 QueryClient 연결
setQueryClient(queryClient)

function App() {
  const [showPasscode, setShowPasscode] = useState(false)
  const [passcodeChecked, setPasscodeChecked] = useState(false)
  const [isCheckingPasscode, setIsCheckingPasscode] = useState(false)
  const { hasAppPassword } = useAppStore()

  // 앱 시작 시 네이티브에서 토큰 복원 시도 및 패스코드 확인
  useEffect(() => {
    // 네이티브 브릿지 리스너 초기화

    const initializeApp = async () => {
      await initializeNativeBridgeListeners()
      // localStorage 사용으로 변경 - sessionStorage는 앱 재시작 시 초기화됨
      const hasRestored = safeLocalStorage.get(LOCAL_STORAGE_KEYS.TOKEN_RESTORE_ATTEMPTED)
      const lastRestoreTime = safeLocalStorage.get(LOCAL_STORAGE_KEYS.TOKEN_RESTORE_TIME)
      const now = Date.now()

      // 마지막 복원 시도가 5초 이내면 토큰 복원은 건너뛰지만 FCM은 체크
      if (hasRestored && lastRestoreTime && now - parseInt(lastRestoreTime) < 5000) {
        console.log('앱 시작: 최근에 토큰 복원을 시도했습니다')

        // 토큰이 있는지 확인
        const hasToken = !!useAuthStore.getState().getAccessToken()
        console.log('[중복 체크] 토큰 존재 여부:', hasToken)

        if (hasToken) {
          // 토큰이 있으면 FCM만 체크하고 종료
          console.log('[FCM 체크] isWebView():', isWebView())
          console.log('[FCM 체크] getAccessToken():', !!useAuthStore.getState().getAccessToken())

          if (isWebView()) {
            console.log('이미 로그인된 상태에서 FCM 토큰 등록 시작...')
            setTimeout(() => {
              handlePostLoginFcmSetup().catch(error => {
                console.error('FCM 토큰 등록 실패:', error)
              })
            }, 1000)
          }

          // 최근에 복원을 시도했으면 패스코드 확인도 건너뛰기
          setPasscodeChecked(true)
          return
        } else {
          // 토큰이 없으면 5초 체크를 무시하고 토큰 대기 로직 실행
          console.log('⚠️ [중복 체크] 토큰 없음 - 토큰 대기 로직 실행')
        }
      }

      try {
        // 복원 시도 표시
        safeLocalStorage.set(LOCAL_STORAGE_KEYS.TOKEN_RESTORE_ATTEMPTED, 'true')
        safeLocalStorage.set(LOCAL_STORAGE_KEYS.TOKEN_RESTORE_TIME, now.toString())

        // ✅ WebView 환경에서는 Native에 명시적으로 인증 상태를 요청
        let restored = false
        if (isWebView()) {
          // 로그인/회원가입 페이지에서는 자동 로그인 시도 건너뛰기
          const currentPath = window.location.pathname
          if (currentPath.startsWith('/auth/')) {
            console.log('⏭️ [App] 인증 페이지에서는 토큰 복원 건너뜀:', currentPath)
          } else {
            console.log('🔄 [App] Native에 인증 상태 요청 중...')

            // Native에게 명시적으로 인증 상태 요청
            restored = await new Promise<boolean>(resolve => {
              let responseReceived = false

              // AUTH:STATUS_RESPONSE 메시지 리스너 등록
              const messageHandler = (message: any) => {
                if (message.type === 'AUTH:STATUS_RESPONSE') {
                  console.log('📩 [App] Native 인증 상태 응답:', message.data)
                  responseReceived = true

                  if (message.data.hasToken && message.data.accessToken) {
                    console.log('✅ [App] Native에 토큰 있음 - 자동 로그인 진행')
                    console.log(decodeJWT(message.data.accessToken))
                    // 토큰이 있으면 저장 (Native가 AUTH:SIGNED_IN도 보낼 예정)
                    resolve(true)
                  } else {
                    console.log('❌ [App] Native에 토큰 없음 - 로그인 필요')
                    resolve(false)
                  }
                }
              }

              // 리스너 등록
              if (window.ReactNativeBridge) {
                window.ReactNativeBridge.onMessage(messageHandler)
              }

              // Native에 인증 상태 요청
              try {
                if (window.ReactNativeBridge) {
                  window.ReactNativeBridge.sendMessage('REQUEST_AUTH_STATUS', {
                    timestamp: Date.now(),
                    currentPath: window.location.pathname
                  })
                  console.log('📤 [App] REQUEST_AUTH_STATUS 전송 완료')
                } else {
                  console.error('❌ [App] ReactNativeBridge 없음')
                  resolve(false)
                }
              } catch (error) {
                console.error('❌ [App] REQUEST_AUTH_STATUS 전송 실패:', error)
                resolve(false)
              }

              // 네트워크 타임아웃 (30초) - 실제 대기가 아닌 실패 대비용
              setTimeout(() => {
                if (!responseReceived) {
                  console.error('⚠️ [App] Native 응답 타임아웃 (30초) - 네트워크 문제 의심')
                  resolve(false)
                }
              }, 30000)
            })
          }
        }

        if (restored) {
          console.log('앱 시작: 토큰 복원 성공')
          // 토큰이 복원되면 현재 페이지를 유지하면서 인증 상태 업데이트
          // 리로드 대신 라우터를 통해 이동
          const currentPath = window.location.pathname
          // TOS, signup 페이지는 제외하고 리다이렉트
          if (
            currentPath.startsWith('/auth/') &&
            !currentPath.includes('/auth/tos') &&
            !currentPath.includes('/auth/signup')
          ) {
            window.location.href = '/'
          }

          // 토큰 복원 성공 시 FCM 토큰도 서버에 등록
          console.log('[토큰 복원 후 FCM 체크] isWebView():', isWebView())
          console.log('[토큰 복원 후 FCM 체크] window.ReactNativeWebView:', !!window.ReactNativeWebView)
          console.log('[토큰 복원 후 FCM 체크] window.ReactNativeBridge:', !!window.ReactNativeBridge)

          if (isWebView()) {
            console.log('토큰 복원 후 FCM 토큰 등록 시작...')
            // 비동기로 실행하여 UI 블로킹 방지
            setTimeout(() => {
              handlePostLoginFcmSetup().catch(error => {
                console.error('토큰 복원 후 FCM 토큰 등록 실패:', error)
              })
            }, 1000)
          }
        } else {
          console.log('앱 시작: 복원할 토큰 없음')
          // 복원할 토큰이 없으면 플래그 제거
          safeLocalStorage.remove(LOCAL_STORAGE_KEYS.TOKEN_RESTORE_ATTEMPTED)
          safeLocalStorage.remove(LOCAL_STORAGE_KEYS.TOKEN_RESTORE_TIME)
        }
      } catch (error) {
        console.error('앱 시작: 토큰 복원 실패', error)
        // 실패 시 플래그 제거
        safeLocalStorage.remove(LOCAL_STORAGE_KEYS.TOKEN_RESTORE_ATTEMPTED)
        safeLocalStorage.remove(LOCAL_STORAGE_KEYS.TOKEN_RESTORE_TIME)
      }

      // 패스코드 확인 로직
      await checkPasscodeStatus()

      // WebView 환경에서 로그인된 사용자의 FCM 토큰 등록
      // 토큰 복원 직후가 아닌 경우에만 실행 (중복 방지)
      const recentlyRestored = safeLocalStorage.get(LOCAL_STORAGE_KEYS.TOKEN_RESTORE_ATTEMPTED)
      if (isWebView() && useAuthStore.getState().getAccessToken() && !recentlyRestored) {
        try {
          console.log('initializeApp: 로그인된 사용자의 FCM 토큰 등록 시작...')
          await handlePostLoginFcmSetup()
        } catch (error) {
          console.error('initializeApp: Failed to setup FCM token:', error)
        }
      }
    }

    const checkPasscodeStatus = async () => {
      // 이미 패스코드 확인 중이면 중복 실행 방지
      if (isCheckingPasscode) {
        return
      }

      setIsCheckingPasscode(true)

      // 로그아웃 중인 경우 패스코드 확인 불필요
      if (safeSessionStorage.get(SESSION_STORAGE_KEYS.IS_LOGGING_OUT) === 'true') {
        safeSessionStorage.remove(SESSION_STORAGE_KEYS.IS_LOGGING_OUT)
        setPasscodeChecked(true)
        setIsCheckingPasscode(false)
        return
      }

      // 로그인 상태 확인 - 토큰이 없으면 즉시 종료
      const accessToken = useAuthStore.getState().getAccessToken()
      if (!accessToken) {
        console.log('패스코드 확인 건너뜀: 로그인되지 않음')
        // 로그인하지 않은 경우 패스코드 확인 불필요
        setPasscodeChecked(true)
        setIsCheckingPasscode(false)
        return
      }

      // 인증 페이지인 경우 패스코드 확인 불필요
      const currentPath = window.location.pathname
      if (currentPath.startsWith('/auth/')) {
        console.log('패스코드 확인 건너뜀: 인증 페이지')
        setPasscodeChecked(true)
        setIsCheckingPasscode(false)
        return
      }

      // 로그인 상태에서만 패스코드 설정 여부 확인
      try {
        // 웹뷰 환경에서는 네이티브 앱이 자체적으로 패스코드를 처리한다고 가정
        // 웹뷰에서는 웹 레벨에서 패스코드를 확인하지 않음
        if (isWebView()) {
          console.log('웹뷰 환경: 네이티브 앱에서 패스코드 처리')
          setPasscodeChecked(true)
        } else {
          // 웹 환경: Zustand store 확인
          if (hasAppPassword()) {
            // 패스코드가 설정되어 있으면 패스코드 화면 표시
            setShowPasscode(true)
          } else {
            // 패스코드가 설정되어 있지 않으면 바로 진행
            setPasscodeChecked(true)
          }
        }
      } catch (error) {
        console.error('패스코드 상태 확인 실패:', error)
        setPasscodeChecked(true)
      } finally {
        setIsCheckingPasscode(false)
      }
    }

    // Native 브릿지 초기화
    initializeApp()

    // cleanup
    return () => {
      // cleanup 작업이 필요한 경우 여기에 추가
    }
  }, [])

  const handlePasscodeSuccess = () => {
    setShowPasscode(false)
    setPasscodeChecked(true)
  }

  // 패스코드 화면 표시
  if (showPasscode) {
    return <PasscodeScreen onSuccess={handlePasscodeSuccess} />
  }

  // 패스코드 확인이 완료되지 않았으면 로딩 화면
  if (!passcodeChecked) {
    return (
      <div className='fixed inset-0 bg-white flex items-center justify-center'>
        <div className='text-center'>
          <p className='text-gray-600'>로딩 중...</p>
        </div>
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        {/* 개발 환경에서만 React Query DevTools 표시 */}
        {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
